import { limitCheckApi } from '../services/limitsApi';
import { FeatureType, LimitCheckResult, FeatureAccessResult } from '../types/limits';

// Cache configuration
const CACHE_EXPIRY = 5 * 60 * 1000; // 5 minutes
const creditCheckCache = new Map<string, { result: LimitCheckResult; timestamp: number }>();
const featureAccessCache = new Map<string, { result: FeatureAccessResult; timestamp: number }>();

// Cache key generators
const getCreditCheckKey = (userId: number, orgId: number, creditAmount: number) => 
  `credit-${userId}-${orgId}-${creditAmount}`;

const getFeatureAccessKey = (userId: number, orgId: number, feature: FeatureType) => 
  `feature-${userId}-${orgId}-${feature}`;

// Cache cleanup function
const cleanExpiredCache = () => {
  const now = Date.now();
  
  // Clean credit check cache
  creditCheckCache.forEach((value, key) => {
    if (now - value.timestamp > CACHE_EXPIRY) {
      creditCheckCache.delete(key);
    }
  });
  
  // Clean feature access cache
  featureAccessCache.forEach((value, key) => {
    if (now - value.timestamp > CACHE_EXPIRY) {
      featureAccessCache.delete(key);
    }
  });
};

// Run cache cleanup every minute
setInterval(cleanExpiredCache, 60 * 1000);

/**
 * Check if user can spend the specified amount of credits
 * Uses caching to improve performance
 */
export const checkUserCreditWithCache = async (
  userId: number, 
  orgId: number, 
  creditAmount: number
): Promise<LimitCheckResult> => {
  const cacheKey = getCreditCheckKey(userId, orgId, creditAmount);
  const cached = creditCheckCache.get(cacheKey);
  
  // Return cached result if still valid
  if (cached && Date.now() - cached.timestamp < CACHE_EXPIRY) {
    return cached.result;
  }
  
  try {
    const result = await limitCheckApi.checkUserCredit(userId, orgId, creditAmount);
    
    // Cache the result
    creditCheckCache.set(cacheKey, {
      result,
      timestamp: Date.now(),
    });
    
    return result;
  } catch (error) {
    console.error('Credit check failed:', error);
    // Return a safe default that blocks the action
    return {
      allowed: false,
      reason: 'Unable to verify credit limit. Please try again.',
    };
  }
};

/**
 * Check if user has access to a specific feature
 * Uses caching to improve performance
 */
export const checkFeatureAccessWithCache = async (
  userId: number, 
  orgId: number, 
  feature: FeatureType
): Promise<FeatureAccessResult> => {
  const cacheKey = getFeatureAccessKey(userId, orgId, feature);
  const cached = featureAccessCache.get(cacheKey);
  
  // Return cached result if still valid
  if (cached && Date.now() - cached.timestamp < CACHE_EXPIRY) {
    return cached.result;
  }
  
  try {
    const result = await limitCheckApi.checkFeatureAccess(userId, orgId, feature);
    
    // Create the feature access result with the feature field
    const featureAccessResult: FeatureAccessResult = {
      ...result,
      feature,
    };
    
    // Cache the result
    featureAccessCache.set(cacheKey, {
      result: featureAccessResult,
      timestamp: Date.now(),
    });
    
    return featureAccessResult;
  } catch (error) {
    console.error('Feature access check failed:', error);
    // Return a safe default that blocks access
    return {
      allowed: false,
      reason: 'Unable to verify feature access. Please try again.',
      feature,
    };
  }
};

/**
 * Batch check multiple features for a user
 */
export const batchCheckFeatureAccess = async (
  userId: number,
  orgId: number,
  features: FeatureType[]
): Promise<Record<FeatureType, FeatureAccessResult>> => {
  const results: Record<string, FeatureAccessResult> = {};
  
  // Check all features in parallel
  const promises = features.map(async (feature) => {
    const result = await checkFeatureAccessWithCache(userId, orgId, feature);
    return { feature, result };
  });
  
  const resolvedResults = await Promise.all(promises);
  
  // Build the results object
  resolvedResults.forEach(({ feature, result }) => {
    results[feature] = result;
  });
  
  return results as Record<FeatureType, FeatureAccessResult>;
};

/**
 * Pre-action validation for expensive operations
 */
export const validateActionCost = async (
  userId: number,
  orgId: number,
  creditCost: number,
  actionName: string
): Promise<{ canProceed: boolean; message: string; showWarning: boolean }> => {
  try {
    const creditCheck = await checkUserCreditWithCache(userId, orgId, creditCost);
    
    if (!creditCheck.allowed) {
      return {
        canProceed: false,
        message: `Cannot perform ${actionName}: ${creditCheck.reason}`,
        showWarning: false,
      };
    }
    
    // Check if user is approaching their limit (within 10% of limit)
    const usagePercentage = creditCheck.current_usage && creditCheck.limit_value 
      ? (creditCheck.current_usage / creditCheck.limit_value) * 100 
      : 0;
    
    if (usagePercentage > 90) {
      return {
        canProceed: true,
        message: `Warning: You are approaching your credit limit. This action will cost ${creditCost} credits.`,
        showWarning: true,
      };
    }
    
    return {
      canProceed: true,
      message: `This action will cost ${creditCost} credits.`,
      showWarning: false,
    };
  } catch (error) {
    return {
      canProceed: false,
      message: `Unable to validate action cost. Please try again.`,
      showWarning: false,
    };
  }
};

/**
 * Get user's current credit status
 */
export const getUserCreditStatus = async (
  userId: number,
  orgId: number
): Promise<{
  current_usage: number;
  remaining: number;
  limit_value: number;
  usage_percentage: number;
  status: 'normal' | 'warning' | 'critical' | 'exceeded';
}> => {
  try {
    const creditCheck = await checkUserCreditWithCache(userId, orgId, 0);
    
    const current_usage = creditCheck.current_usage || 0;
    const limit_value = creditCheck.limit_value || 0;
    const remaining = creditCheck.remaining || 0;
    const usage_percentage = limit_value > 0 ? (current_usage / limit_value) * 100 : 0;
    
    let status: 'normal' | 'warning' | 'critical' | 'exceeded' = 'normal';
    
    if (!creditCheck.allowed) {
      status = 'exceeded';
    } else if (usage_percentage >= 95) {
      status = 'critical';
    } else if (usage_percentage >= 80) {
      status = 'warning';
    }
    
    return {
      current_usage,
      remaining,
      limit_value,
      usage_percentage,
      status,
    };
  } catch (error) {
    console.error('Failed to get credit status:', error);
    return {
      current_usage: 0,
      remaining: 0,
      limit_value: 0,
      usage_percentage: 0,
      status: 'normal',
    };
  }
};

/**
 * Clear cache for specific user/org combination
 */
export const clearUserCache = (userId: number, orgId: number) => {
  const keysToDelete: string[] = [];
  
  // Find all cache keys for this user/org
  creditCheckCache.forEach((value, key) => {
    if (key.includes(`-${userId}-${orgId}`)) {
      keysToDelete.push(key);
    }
  });
  
  featureAccessCache.forEach((value, key) => {
    if (key.includes(`-${userId}-${orgId}`)) {
      keysToDelete.push(key);
    }
  });
  
  // Delete the keys
  keysToDelete.forEach(key => {
    creditCheckCache.delete(key);
    featureAccessCache.delete(key);
  });
};

/**
 * Clear all cache
 */
export const clearAllCache = () => {
  creditCheckCache.clear();
  featureAccessCache.clear();
};

/**
 * Get cache statistics for debugging
 */
export const getCacheStats = () => {
  return {
    creditCheckCacheSize: creditCheckCache.size,
    featureAccessCacheSize: featureAccessCache.size,
    totalCacheSize: creditCheckCache.size + featureAccessCache.size,
  };
};

/**
 * Format credit amount for display
 */
export const formatCredits = (credits: number): string => {
  if (credits >= 1000000) {
    return `${(credits / 1000000).toFixed(2)}M`;
  } else if (credits >= 1000) {
    return `${(credits / 1000).toFixed(1)}K`;
  }
  return credits.toFixed(1);
};

/**
 * Format currency equivalent for credits
 */
export const formatCreditsCurrency = (credits: number): string => {
  return `$${credits.toFixed(2)}`;
};

/**
 * Get status color for UI components
 */
export const getStatusColor = (status: 'normal' | 'warning' | 'critical' | 'exceeded'): string => {
  switch (status) {
    case 'normal':
      return 'text-green-600 dark:text-green-400';
    case 'warning':
      return 'text-yellow-600 dark:text-yellow-400';
    case 'critical':
      return 'text-orange-600 dark:text-orange-400';
    case 'exceeded':
      return 'text-red-600 dark:text-red-400';
    default:
      return 'text-gray-600 dark:text-gray-400';
  }
};

/**
 * Get status background color for UI components
 */
export const getStatusBgColor = (status: 'normal' | 'warning' | 'critical' | 'exceeded'): string => {
  switch (status) {
    case 'normal':
      return 'bg-green-100 dark:bg-green-900/20';
    case 'warning':
      return 'bg-yellow-100 dark:bg-yellow-900/20';
    case 'critical':
      return 'bg-orange-100 dark:bg-orange-900/20';
    case 'exceeded':
      return 'bg-red-100 dark:bg-red-900/20';
    default:
      return 'bg-gray-100 dark:bg-gray-900/20';
  }
};
